import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import type { CaptureLatencyInputT, IncrementCountInputT, PerformanceMetricActionT } from '../../image/labelling-v2/analytics/types/performance';
import { PERFORMANCE_METRIC_EVENTS } from '../../image/labelling-v2/constants/performance';
import { extractFrameProcessorErrorCategory } from '../../image/labelling-v2/utils/frame-processor-error-utils';
import { FRAME_DETAILS_STATUS, FRAME_PROCESSOR_INPUT_MESSAGES, FRAME_PROCESSOR_OUTPUT_MESSAGES } from '../../workers/constants/frame-processor-constants';
import type { WebWorker } from '../../workers/types/common';
import type { FrameDetailsState, PTSTimestampTable, VideoFrameProcessorWorkerOutput } from '../../workers/types/video-frame-processor';
type FrameDetailsResult = {
  frameCount: number;
  frameTable: PTSTimestampTable;
};
type FrameDetailsExtractorPropsT = {
  videoFrameProcessor?: WebWorker;
  onFrameDetailsExtracted?: (result: FrameDetailsResult) => void;
  onFrameExtractionError?: (error: string) => void;
  onStateChange?: (state: FrameDetailsState) => void;
  /** Optional performance metrics callback for async timing. */
  measureLatency?: (name: PerformanceMetricActionT) => () => void;
  /** Optional performance metrics callback for timing. */
  captureLatency?: (input: CaptureLatencyInputT) => void;
  /** Optional performance metrics callback for error counting. */
  incrementCount?: (input: IncrementCountInputT) => void;
};
type FrameDetailsExtractorResultT = {
  extractFrameDetails: (videoUrl: string, mediaFileReference: string) => void;
  cancelExtraction: () => void;
};
export function useFrameDetailsExtractor({
  videoFrameProcessor,
  onFrameDetailsExtracted,
  onFrameExtractionError,
  onStateChange,
  measureLatency,
  captureLatency,
  incrementCount
}: FrameDetailsExtractorPropsT): FrameDetailsExtractorResultT {
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isExtractingRef = useRef(false);
  const currentUrlRef = useRef<string | null>(null);
  const currentRequestIdRef = useRef<string | null>(null);
  const onFrameDetailsExtractedRef = useRef(onFrameDetailsExtracted);
  // eslint-disable-next-line react-hooks/refs
  onFrameDetailsExtractedRef.current = onFrameDetailsExtracted;
  const onFrameExtractionErrorRef = useRef(onFrameExtractionError);
  // eslint-disable-next-line react-hooks/refs
  onFrameExtractionErrorRef.current = onFrameExtractionError;
  const onStateChangeRef = useRef(onStateChange);
  // eslint-disable-next-line react-hooks/refs
  onStateChangeRef.current = onStateChange;
  const startMeasureLatencyRef = useRef(measureLatency);
  // eslint-disable-next-line react-hooks/refs
  startMeasureLatencyRef.current = measureLatency;
  const commitMeasureLatencyRef = useRef<(() => void) | null>(null);
  const captureLatencyRef = useRef(captureLatency);
  // eslint-disable-next-line react-hooks/refs
  captureLatencyRef.current = captureLatency;
  const incrementCountRef = useRef(incrementCount);
  // eslint-disable-next-line react-hooks/refs
  incrementCountRef.current = incrementCount;
  const cleanupSubscription = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
  }, []);
  const finish = useCallback((state?: FrameDetailsState) => {
    isExtractingRef.current = false;
    currentUrlRef.current = null;
    cleanupSubscription();
    if (state) onStateChangeRef.current?.(state);
  }, [cleanupSubscription]);
  const extractFrameDetails = useCallback((videoUrl: string, mediaFileReference: string) => {
    if (isExtractingRef.current && currentUrlRef.current === videoUrl) return;
    if (!videoFrameProcessor) return;
    if (!videoFrameProcessor.isReady()) {
      const workerError = videoFrameProcessor.getError();
      const errorMessage = workerError?.message ?? 'Video frame processor is not ready';
      finish({
        status: FRAME_DETAILS_STATUS.ERROR,
        error: errorMessage,
        mediaFileReference
      });
      onFrameExtractionErrorRef.current?.(errorMessage);
      return;
    }
    const requestId = uuid();
    currentRequestIdRef.current = requestId;
    isExtractingRef.current = true;
    currentUrlRef.current = videoUrl;
    onStateChangeRef.current?.({
      status: FRAME_DETAILS_STATUS.LOADING,
      progress: 0,
      mediaFileReference
    });
    cleanupSubscription();
    const handleWorkerMessage = (message: VideoFrameProcessorWorkerOutput) => {
      // Ignore messages from stale requests
      if (message.payload.requestId !== requestId) return;
      if (!isExtractingRef.current) return;
      switch (message.type) {
        case FRAME_PROCESSOR_OUTPUT_MESSAGES.PROGRESS:
          onStateChangeRef.current?.({
            status: FRAME_DETAILS_STATUS.LOADING,
            progress: message.payload.percent,
            mediaFileReference
          });
          break;
        case FRAME_PROCESSOR_OUTPUT_MESSAGES.FRAME_DETAILS:
          commitMeasureLatencyRef.current?.();
          commitMeasureLatencyRef.current = null;
          if (captureLatencyRef.current && message.payload.storageTimeMs > 0) {
            captureLatencyRef.current({
              name: PERFORMANCE_METRIC_EVENTS.FRAME_EXTRACTION_STORAGE_TIME,
              durationMs: message.payload.storageTimeMs
            });
          }
          finish({
            status: FRAME_DETAILS_STATUS.SUCCESS,
            data: {
              frameTable: message.payload.frameTable,
              accurateFrameCount: message.payload.frameCount
            },
            mediaFileReference
          });
          onFrameDetailsExtractedRef.current?.({
            frameCount: message.payload.frameCount,
            frameTable: message.payload.frameTable
          });
          break;
        case FRAME_PROCESSOR_OUTPUT_MESSAGES.ERROR:
          if (incrementCountRef.current) {
            incrementCountRef.current({
              name: PERFORMANCE_METRIC_EVENTS.FRAME_EXTRACTION_ERROR,
              tags: {
                errorCategory: extractFrameProcessorErrorCategory(message.payload.message),
                errorMessage: message.payload.message
              }
            });
          }
          finish({
            status: FRAME_DETAILS_STATUS.ERROR,
            error: message.payload.message,
            mediaFileReference
          });
          onFrameExtractionErrorRef.current?.(message.payload.message);
          break;
        default:
          break;
      }
    };
    unsubscribeRef.current = videoFrameProcessor.subscribe(handleWorkerMessage);
    console.info('[AccurateFrameRendering] Extracting precise frames via mp4 demuxer', {
      videoUrl,
      mediaFileReference,
      requestId
    });
    if (startMeasureLatencyRef.current) {
      commitMeasureLatencyRef.current = startMeasureLatencyRef.current(PERFORMANCE_METRIC_EVENTS.FRAME_EXTRACTION_TOTAL_TIME);
    }
    videoFrameProcessor.postMessage({
      type: FRAME_PROCESSOR_INPUT_MESSAGES.EXTRACT_FRAMES,
      payload: {
        videoUrl,
        mediaFileReference,
        requestId,
        cacheFrames: true
      }
    });
  }, [videoFrameProcessor, cleanupSubscription, finish]);
  const cancelExtraction = useCallback(() => {
    if (!videoFrameProcessor) return;
    currentRequestIdRef.current = null;
    videoFrameProcessor.postMessage({
      type: FRAME_PROCESSOR_INPUT_MESSAGES.CANCEL
    });
    finish({
      status: FRAME_DETAILS_STATUS.IDLE
    });
  }, [videoFrameProcessor, finish]);
  useEffect(() => {
    return () => {
      currentRequestIdRef.current = null;
      finish({
        status: FRAME_DETAILS_STATUS.IDLE
      });
    };
  }, [finish]);
  return {
    extractFrameDetails,
    cancelExtraction
  };
}